import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { ArrowRightIcon } from "styles/icons";
import { useStickyBottomBarContext } from "contexts/StickyBottomBarContext";
import { Fade, Flex, Text, Button, Box, CloseButton } from "@chakra-ui/react";
import { StickyBottomBarAhPlusLogo } from "./StickyBottomBarAhPlusLogo";
import { stickyBottomBarMobileStyles } from "./StickyBottomBarMobile.styles";
import { useDynamicIndex } from "./StickyBottomBarMobile.hook";
import type { StickyBottomBarIconProps, StickyBottomBarProps } from "../StickyBottomBar.d";
import { getCTAElementProps } from "../utils/getCTAElementProps";

const StickyBottomBarMobile = ({
  iconsWithText,
  variant,
  heading,
}: {
  iconsWithText: StickyBottomBarIconProps[];
  variant: StickyBottomBarProps["variant"];
  heading: StickyBottomBarProps["heading"];
}) => {
  const language = useLanguage();
  const index = useDynamicIndex(iconsWithText.length);
  const translations = useTranslations();
  const { closeBottomBar } = useStickyBottomBarContext();
  const Icon = iconsWithText[index].icon;
  const CTAElementProps = getCTAElementProps(language, variant);

  const actionButton = {
    basic: (
      <Box sx={stickyBottomBarMobileStyles[variant].actionButton} pointerEvents="none">
        <Fade in key={iconsWithText[index].textKey}>
          <Text color="greyscale.100" fontSize="sm" fontWeight="bold">
            {translations(iconsWithText[index].textKey)} <ArrowRightIcon ml="6px" />
          </Text>
        </Fade>
      </Box>
    ),
    ahPlus: (
      <Button size="xs" sx={stickyBottomBarMobileStyles[variant].actionButton} pointerEvents="none">
        <ArrowRightIcon />
      </Button>
    ),
  }[variant];

  const customHeading = {
    basic: translations("check_compensation"),
    ahPlus: heading || translations("be_prepared_for_future_flight_disruptions"),
  }[variant];

  return (
    <Flex
      align="flex-start"
      px={4}
      py={5}
      borderRadius="lg"
      mx={4}
      mb={4}
      sx={stickyBottomBarMobileStyles[variant].container}
    >
      <Flex as="a" sx={stickyBottomBarMobileStyles[variant].iconsWithText} {...CTAElementProps}>
        <Fade in key={iconsWithText[index].textKey}>
          <Flex borderRadius="50%" border="2px solid" borderColor="primary.400" mr={3} p={2}>
            <Icon boxSize={5} />
          </Flex>
        </Fade>
      </Flex>
      <Flex as="a" sx={stickyBottomBarMobileStyles[variant].innerContainer} {...CTAElementProps}>
        <StickyBottomBarAhPlusLogo mr={3} sx={stickyBottomBarMobileStyles[variant].logo} />
        <Text as="span" fontWeight={700} sx={stickyBottomBarMobileStyles[variant].heading}>
          {customHeading}
        </Text>
        {actionButton}
      </Flex>
      <CloseButton
        sx={stickyBottomBarMobileStyles[variant].closeButton}
        onClick={closeBottomBar}
        aria-label="Close bottom bar"
      />
    </Flex>
  );
};

export default StickyBottomBarMobile;
